import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, FileText, Edit3, Check, X, Calendar,
  User as UserIcon, Baby, AlertCircle, Mail, Shield,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useUserAuth } from "../../context/UserAuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sprint 7D — Mini-portal do EE: dedicated view for guardians showing all
 * minor tickets they purchased, with one-click PDF download + minor name edit.
 */
const FamilyPortal = () => {
  const { user, loading: authLoading } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/users/me/family`, { withCredentials: true });
      setChildren(data.children || []);
    } catch (e) {
      if (e.response?.status === 401) toast.error("Sessão expirada");
      else toast.error(e.response?.data?.detail || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user) load();
    if (!authLoading && !user) setLoading(false);
  }, [authLoading, user]);

  const downloadPdf = (childId, ticketNumber) => {
    const url = `${API_URL}/api/users/me/family/${childId}/parental-pdf`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `Termo_Responsabilidade_${ticketNumber || childId}.pdf`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const startEdit = (child) => {
    setEditingId(child.id);
    setEditingName(child.minor.full_name || "");
  };

  const saveEdit = async () => {
    if (!editingName.trim() || editingName.trim().length < 2) {
      toast.error("Nome inválido");
      return;
    }
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/users/me/family/${editingId}/minor`,
        { full_name: editingName.trim() },
        { withCredentials: true },
      );
      toast.success("Nome do menor actualizado.");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-300 mb-4">Precisa de entrar na sua conta para aceder ao portal.</p>
          <Link to="/login" className="text-amber-400 hover:text-amber-300 underline">Entrar</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/conta" className="text-zinc-400 hover:text-zinc-200 flex items-center gap-1 text-sm" data-testid="family-back">
            <ChevronLeft className="w-4 h-4" /> Voltar à minha conta
          </Link>
        </div>

        <div className="mb-8">
          <p className="text-cyan-400 text-[10px] uppercase tracking-[3px] font-semibold flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Portal Família</p>
          <h1 className="text-3xl font-bold text-white mt-2">Os bilhetes dos seus menores</h1>
          <p className="text-zinc-400 mt-2 text-sm">
            Como Encarregado de Educação, aqui vê todos os bilhetes de menores que comprou.
            Pode reimprimir o Termo de Responsabilidade ou corrigir o nome do menor a qualquer momento (antes do check-in).
          </p>
        </div>

        {children.length === 0 ? (
          <div className="bg-zinc-900 border border-white/10 rounded-2xl p-10 text-center" data-testid="family-empty">
            <Baby className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-300 font-medium">Ainda não tem bilhetes de menores</p>
            <p className="text-zinc-500 text-sm mt-1">Compras de bilhetes infantis aparecerão automaticamente aqui.</p>
            <Link to="/" className="inline-block mt-4 text-amber-400 hover:text-amber-300 underline text-sm">
              Ver bilhetes disponíveis
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="family-list">
            {children.map((c) => {
              const isU12 = c.minor?.category === "child_under_12";
              const ageNow = c.minor?.current_age ?? c.minor?.age;
              const statusBadge = c.checked_in
                ? { lbl: "Check-in feito", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" }
                : c.qr_issued
                  ? { lbl: "Bilhete emitido", cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" }
                  : c.is_installment_plan
                    ? { lbl: "Plano em curso", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" }
                    : c.payment_status === "paid"
                      ? { lbl: "Pago", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" }
                      : { lbl: "Pendente", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" };

              return (
                <div key={c.id} className="bg-zinc-900 border border-white/10 rounded-2xl p-5 hover:border-cyan-500/30 transition-colors" data-testid={`family-card-${c.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/30 to-pink-500/30 border border-cyan-500/30 flex items-center justify-center text-2xl">
                        {isU12 ? "👶" : "🧒"}
                      </div>
                      <div>
                        {editingId === c.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              className="input-dark h-8 text-sm w-44"
                              autoFocus
                              data-testid={`family-edit-input-${c.id}`}
                            />
                            <button onClick={saveEdit} disabled={saving} className="p-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300" data-testid={`family-edit-save-${c.id}`}>
                              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            </button>
                            <button onClick={() => setEditingId(null)} className="p-1.5 rounded bg-zinc-700 hover:bg-zinc-600 text-zinc-300">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <p className="text-white font-semibold flex items-center gap-1.5">
                            {c.minor?.full_name || "—"}
                            {!c.checked_in && (
                              <button onClick={() => startEdit(c)} className="text-zinc-600 hover:text-cyan-400" title="Editar nome" data-testid={`family-edit-${c.id}`}>
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </p>
                        )}
                        <p className="text-zinc-500 text-xs">
                          {ageNow != null ? `${ageNow} anos · ` : ""}{isU12 ? "Criança ≤12" : "Jovem 13-17"}
                        </p>
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${statusBadge.cls}`}>
                      {statusBadge.lbl}
                    </span>
                  </div>

                  <div className="space-y-1 mb-3 text-xs">
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{c.session_date || "—"}{c.session_time ? ` · ${c.session_time}` : ""}</span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-400">
                      <FileText className="w-3.5 h-3.5" />
                      <span className="font-mono text-amber-300/70">{c.ticket_number}</span>
                      <span className="text-zinc-600">·</span>
                      <span>{c.ticket_type_name}</span>
                    </div>
                    {c.responsible?.full_name && (
                      <div className="flex items-center gap-2 text-zinc-400">
                        <UserIcon className="w-3.5 h-3.5" />
                        <span>{isU12 ? "Acompanhante" : "Responsável"}: {c.responsible.full_name}</span>
                      </div>
                    )}
                    {c.parental_email_sent_at && (
                      <div className="flex items-center gap-2 text-emerald-400 text-[11px]">
                        <Mail className="w-3 h-3" />
                        <span>Termo enviado em {new Date(c.parental_email_sent_at).toLocaleDateString("pt-PT")}</span>
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => downloadPdf(c.id, c.ticket_number)}
                    className="w-full bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 border border-cyan-500/30"
                    data-testid={`family-download-${c.id}`}
                  >
                    <FileText className="w-4 h-4 mr-2" /> Descarregar Termo (PDF)
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-8 bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
          <p className="text-amber-200 text-xs">
            <strong>Nota legal:</strong> Os Termos de Responsabilidade já submetidos têm validade legal e
            não podem ser alterados após o pagamento. Pode no entanto corrigir o nome do menor (typos)
            antes do check-in. Para alterar a data de nascimento ou o adulto responsável, contacte o admin.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FamilyPortal;
